/**
 * AuditTimelineView — visão cronológica agregada (A5).
 *
 * Consome `GET /v1/audit-events/timeline` (buckets day|hour). Renderiza barras
 * por período com a contagem total + breakdown por severidade. É agregação pura
 * (o serializer não devolve eventos aqui), fiel ao endpoint real.
 */

import { SEVERITY_BADGE, SEVERITY_LABEL, formatBucketLabel } from './audit-labels';

import type { AuditSeverity, AuditTimelineResultWire } from './types';

interface Props {
  data: AuditTimelineResultWire | undefined;
  isLoading: boolean;
  isError: boolean;
  bucket: 'day' | 'hour';
  onBucketChange: (bucket: 'day' | 'hour') => void;
}

export function AuditTimelineView({
  data,
  isLoading,
  isError,
  bucket,
  onBucketChange,
}: Props): JSX.Element {
  const maxCount = data?.buckets.reduce((m, b) => Math.max(m, b.count), 0) ?? 0;

  return (
    <div className="px-8 py-4">
      <div className="mb-3 flex items-center gap-2">
        <span className="text-xs text-text-secondary">Agrupar por:</span>
        <div className="inline-flex overflow-hidden rounded-md border border-border-subtle">
          {(['day', 'hour'] as const).map((b) => (
            <button
              key={b}
              type="button"
              onClick={() => onBucketChange(b)}
              className={`px-3 py-1 text-xs ${
                bucket === b
                  ? 'bg-accent text-white'
                  : 'bg-surface text-text-secondary hover:bg-surface-raised'
              }`}
            >
              {b === 'day' ? 'Dia' : 'Hora'}
            </button>
          ))}
        </div>
        {data !== undefined && (
          <span className="ml-2 font-mono text-[11px] text-text-secondary">
            {data.total} eventos na janela
          </span>
        )}
      </div>

      {isError && (
        <p role="alert" className="text-sm text-red-600">
          Não foi possível carregar a timeline.
        </p>
      )}
      {isLoading && <p className="text-sm text-text-secondary">Carregando timeline…</p>}

      {!isLoading && !isError && data?.buckets.length === 0 && (
        <p className="py-8 text-center text-sm text-text-secondary">
          Nenhum evento na janela selecionada.
        </p>
      )}

      {!isLoading && !isError && data !== undefined && data.buckets.length > 0 && (
        <ul className="flex flex-col gap-2">
          {data.buckets.map((b) => (
            <li key={b.periodStart} className="flex items-center gap-3">
              <span className="w-32 shrink-0 text-xs text-text-secondary">
                {formatBucketLabel(b.periodStart, bucket)}
              </span>
              <div className="flex h-5 flex-1 items-center overflow-hidden rounded bg-surface-raised">
                <div
                  className="h-full rounded bg-accent/30"
                  style={{ width: maxCount > 0 ? `${(b.count / maxCount) * 100}%` : '0%' }}
                />
              </div>
              <span className="w-10 shrink-0 text-right font-mono text-xs text-text-primary">
                {b.count}
              </span>
              <span className="flex shrink-0 gap-1">
                {(Object.entries(b.bySeverity) as [AuditSeverity, number][]).map(([sev, n]) => (
                  <span
                    key={sev}
                    className={`rounded-full px-1.5 py-0.5 text-[10px] font-medium ${
                      SEVERITY_BADGE[sev] ?? 'bg-gray-100 text-gray-600'
                    }`}
                    title={`${SEVERITY_LABEL[sev] ?? sev}: ${n}`}
                  >
                    {SEVERITY_LABEL[sev] ?? sev} {n}
                  </span>
                ))}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
